import React, { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import dayjs from 'dayjs'

import { getErrorMessage, relativeOrDateTime } from 'utils'

import { useSnackbar } from 'contexts'
import { useExtendDemo } from 'hooks/api'
import { DemoListItem } from 'services/demosApi'
import { DEMO_LIFETIME_OPTIONS, DemoLifetimeValue } from 'constants/demos'

import { Stack, Typography } from '@mui/material'

import { Button, Modal } from '@repo/ui'
import { CompanyBlock, DatePickerField, SelectField } from 'components'

interface ExtendDemoModalProps {
  demo: DemoListItem | null
  open: boolean
  onClose: () => void
}

type ExtendLifetime = DemoLifetimeValue | 'custom'

interface ExtendFormValues {
  lifetime: ExtendLifetime
  customDate: string
}

/**
 * The four presets, plus a custom date.
 *
 * Appended here rather than added to the shared `DEMO_LIFETIME_OPTIONS`, because
 * that list is also what CREATE renders and adding an option there would change
 * the provisioning flow nobody asked to change. The presets stay one source; only
 * the extra entry is local.
 */
const EXTEND_LIFETIME_OPTIONS = [
  ...DEMO_LIFETIME_OPTIONS,
  { label: 'Custom date', value: 'custom' as const },
]

const DATE_FORMAT = 'YYYY-MM-DD'

/**
 * The lifetime the modal opens on.
 *
 * Deliberately always "Always" rather than reverse-engineering the current
 * setting back into one of the presets. A demo expiring in 11 days matches no
 * option, so any guess either silently rounds the AE's existing choice or
 * preselects something they never picked. The CURRENT expiry is shown as text
 * instead, and the dropdown states only what it will BECOME.
 */
const DEFAULTS: ExtendFormValues = { lifetime: 'always', customDate: '' }

/**
 * Presets are measured from NOW, not from the existing expiry.
 *
 * "30 days" on a demo already expiring in 20 means 30 days from today, not 50.
 * That is what "extend it by a month" means to the person clicking it, and the
 * alternative compounds invisibly each time the action is used.
 *
 * Returns `null` for "Always", never `undefined`: the endpoint requires the field
 * to be PRESENT and rejects an absent one, so a serialization slip cannot quietly
 * make a demo permanent. `constants/demos`'s `lifetimeToExpiresAt` returns
 * `undefined` for the same case and is left alone — on PROVISION, omitted really
 * does mean never (VRG-305 decision 9), so each is right for its own path.
 */
const toExpiryIso = (values: ExtendFormValues): string | null => {
  if (values.lifetime === 'always') return null
  if (values.lifetime === 'custom') {
    if (!values.customDate) return null
    // End of the chosen day, so "expires the 30th" includes the 30th rather than
    // lapsing at midnight as that day begins.
    return dayjs(values.customDate).endOf('day').toDate().toISOString()
  }
  const days = Number(values.lifetime)
  return new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString()
}

/**
 * VRG-361 — change a demo's lifetime after it has been issued.
 *
 * The alternative was purge-and-re-provision, which throws away the engagement
 * history that is the record's entire CRM value. A Tier-1 package sitting
 * unopened on a desk for three weeks is the normal case this exists for.
 */
export const ExtendDemoModal = ({ demo, open, onClose }: ExtendDemoModalProps) => {
  const { open: notify } = useSnackbar()
  const { mutate: extend, isPending } = useExtendDemo()

  const methods = useForm<ExtendFormValues>({ defaultValues: DEFAULTS })
  const { control, watch, reset, handleSubmit } = methods
  // DatePickerField types its props against `UseFormReturn` with RHF's default
  // untyped `FieldValues` generic (components/input/fields/types.ts), which a
  // concrete `UseFormReturn<ExtendFormValues>` is not structurally assignable
  // to. Same narrowing CreateDemoModal uses, for the same reason.
  const untypedFieldMethods: any = methods

  const lifetime = watch('lifetime')
  const customDate = watch('customDate')

  // Reset per opening. Without this the modal reopens holding whatever was picked
  // for the PREVIOUS demo, which reads as a preselection of this one's setting.
  useEffect(() => {
    if (open) reset(DEFAULTS)
  }, [open, reset])

  const onValid = (values: ExtendFormValues) => {
    if (!demo) return
    extend(
      { companyId: demo.companyId, expiresAt: toExpiryIso(values) },
      {
        onSuccess: () => {
          notify({ type: 'success', title: 'Demo lifetime updated' })
          onClose()
        },
        onError: (error) => {
          notify({ type: 'error', title: getErrorMessage(error) })
        },
      }
    )
  }

  const current = demo?.demoExpiresAt
    ? relativeOrDateTime(demo.demoExpiresAt)
    : 'Never expires'

  // A custom lifetime with no date chosen would silently send null, i.e. "never",
  // which is the opposite of what picking a date means.
  const incomplete = lifetime === 'custom' && !customDate

  return (
    <Modal
      title="Extend demo"
      open={open}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onValid)}
            disabled={isPending || incomplete}
            loading={isPending}
          >
            Update lifetime
          </Button>
        </>
      }
    >
      <Stack spacing={4}>
        {demo && <CompanyBlock company={{ name: demo.companyName, logo: demo.logo }} />}

        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          Currently: {current}. A preset counts from today, so it replaces the
          existing date rather than adding to it.
        </Typography>

        <Controller
          name="lifetime"
          control={control}
          render={({ field }) => (
            <SelectField
              label="New lifetime"
              value={field.value}
              onChange={(v) => field.onChange(v as ExtendLifetime)}
              options={EXTEND_LIFETIME_OPTIONS}
            />
          )}
        />

        {lifetime === 'custom' && (
          <DatePickerField
            {...untypedFieldMethods}
            name="customDate"
            label="Expires on"
            format={DATE_FORMAT}
            // Yesterday, so TODAY is selectable. A same-day expiry is a
            // legitimate "stop serving this at end of day"; only a date already
            // gone is meaningless to pick from a calendar.
            minDate={dayjs().subtract(1, 'day')}
          />
        )}
      </Stack>
    </Modal>
  )
}
